// In-browser twin of backend/terminal_sim.py for instant terminal feedback.
// The server replays the same command history to grade, so keep both in sync
// (scratch parity test compares them script by script).

export const HOME = "/home/lerner";
const MAX_COMMAND_LENGTH = 300;
const PROTECTED = ["/", "/home", HOME];
const HELP =
  "pwd · ls [ordner] · cd [ordner] · mkdir [-p] · touch · cat · echo · cp [-r] · mv · rm [-r] · " +
  "grep [-i -n -c -v] text [datei] · wc [-l -w -c] · head/tail [-n N] · sort [-r] · uniq · " +
  "Pipes: befehl | befehl · Umleitung: > datei, >> datei · whoami · clear";

export function norm(path) {
  const parts = [];
  path.split("/").forEach((seg) => {
    if (!seg || seg === ".") return;
    if (seg === "..") parts.pop();
    else parts.push(seg);
  });
  return "/" + parts.join("/");
}

export function parentOf(path) {
  const i = path.lastIndexOf("/");
  return i <= 0 ? "/" : path.slice(0, i);
}

const baseName = (path) => path.slice(path.lastIndexOf("/") + 1);
const splitLines = (text) => (text === "" ? [] : text.split("\n"));
const isNumber = (s) => /^[0-9]+$/.test(s);
const isFlag = (a) => a.startsWith("-") && a.length > 1;

/** Words and the operators | > >> (outside quotes); null on an unclosed quote. */
function tokenize(line) {
  const tokens = [];
  let cur = "";
  let quote = null;
  let has = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quote) {
      if (ch === quote) quote = null;
      else cur += ch;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      has = true;
    } else if (" \t|>".includes(ch)) {
      if (cur || has) tokens.push(["word", cur]);
      cur = "";
      has = false;
      if (ch === "|") tokens.push(["op", "|"]);
      else if (ch === ">") {
        if (line[i + 1] === ">") {
          tokens.push(["op", ">>"]);
          i += 1;
        } else tokens.push(["op", ">"]);
      }
    } else cur += ch;
  }
  if (quote) return null;
  if (cur || has) tokens.push(["word", cur]);
  return tokens;
}

function shortFlags(args) {
  const flags = new Set();
  args.filter(isFlag).forEach((a) => [...a.slice(1)].forEach((ch) => flags.add(ch)));
  return flags;
}

export class Shell {
  constructor({ dirs = [], files = {}, cwd = HOME } = {}) {
    this.nodes = new Map([["/", null], ["/home", null], [HOME, null]]); // null = directory
    dirs.forEach((d) => this.mkdirs(norm(d)));
    Object.entries(files).forEach(([path, content]) => {
      const p = norm(path);
      this.mkdirs(parentOf(p));
      this.nodes.set(p, content);
    });
    this.cwd = norm(cwd);
  }

  // ---------------------------------------------------------------- helpers
  mkdirs(path) {
    let cur = "";
    path.split("/").filter(Boolean).forEach((seg) => {
      cur += "/" + seg;
      if (!this.nodes.has(cur)) this.nodes.set(cur, null);
    });
  }

  isDir(p) {
    return this.nodes.has(p) && this.nodes.get(p) === null;
  }

  isFile(p) {
    return this.nodes.has(p) && this.nodes.get(p) !== null;
  }

  resolve(arg) {
    if (!arg || arg === "~") return HOME;
    if (arg.startsWith("~/")) return norm(HOME + arg.slice(1));
    if (arg.startsWith("/")) return norm(arg);
    return norm(this.cwd + "/" + arg);
  }

  children(d) {
    return [...this.nodes.keys()].filter((k) => k !== d && k !== "/" && parentOf(k) === d).sort();
  }

  subtree(p) {
    return [...this.nodes.keys()].filter((k) => k === p || k.startsWith(p + "/")).sort();
  }

  get prompt() {
    const short = this.cwd === HOME || this.cwd.startsWith(HOME + "/") ? "~" + this.cwd.slice(HOME.length) : this.cwd;
    return `lerner@devpath:${short}$ `;
  }

  target(src, dstArg) {
    const dst = this.resolve(dstArg);
    return this.isDir(dst) ? dst + "/" + baseName(src) : dst;
  }

  /** [label, lines] per input source: the named files, else stdin. */
  input(cmd, files, stdin, err) {
    if (files.length) {
      const out = [];
      files.forEach((name) => {
        const p = this.resolve(name);
        if (this.isFile(p)) out.push([name, splitLines(this.nodes.get(p))]);
        else if (this.isDir(p)) err.push(`${cmd}: ${name}: ist ein Verzeichnis`);
        else err.push(`${cmd}: ${name}: Datei nicht gefunden`);
      });
      return out;
    }
    if (stdin !== null) return [["", stdin]];
    err.push(`${cmd}: keine Eingabe (Datei angeben oder per | weiterleiten)`);
    return null;
  }

  // ---------------------------------------------------------------- commands
  exec(argv, stdin) {
    const [cmd, ...args] = argv;
    const flags = shortFlags(args);
    const operands = args.filter((a) => !isFlag(a));
    const out = [];
    const err = [];

    if (cmd === "help") out.push(HELP);
    else if (cmd === "pwd") out.push(this.cwd);
    else if (cmd === "whoami") out.push("lerner");
    else if (cmd === "clear") return { out: [], err: [], clear: true };
    else if (cmd === "ls") {
      const d = this.resolve(operands[0] || ".");
      if (!this.isDir(d)) err.push(`ls: ${operands[0] || d}: Verzeichnis nicht gefunden`);
      else {
        const names = this.children(d).map((k) => baseName(k) + (this.isDir(k) ? "/" : ""));
        if (names.length) out.push(names.join("   "));
      }
    } else if (cmd === "cd") {
      const d = this.resolve(operands[0]);
      if (this.isDir(d)) this.cwd = d;
      else if (this.isFile(d)) err.push(`cd: ${operands[0]}: ist kein Verzeichnis`);
      else err.push(`cd: ${operands[0]}: Verzeichnis nicht gefunden`);
    } else if (cmd === "mkdir") {
      if (!operands.length) err.push("mkdir: Operand fehlt");
      operands.forEach((name) => {
        const p = this.resolve(name);
        if (this.nodes.has(p)) {
          if (!flags.has("p")) err.push(`mkdir: ${name}: existiert bereits`);
        } else if (flags.has("p")) this.mkdirs(p);
        else if (!this.isDir(parentOf(p))) err.push(`mkdir: ${name}: übergeordnetes Verzeichnis fehlt (Tipp: mkdir -p)`);
        else this.nodes.set(p, null);
      });
    } else if (cmd === "touch") {
      if (!operands.length) err.push("touch: Operand fehlt");
      operands.forEach((name) => {
        const p = this.resolve(name);
        if (!this.isDir(parentOf(p))) err.push(`touch: ${name}: Verzeichnis nicht gefunden`);
        else if (!this.nodes.has(p)) this.nodes.set(p, "");
      });
    } else if (cmd === "cat") {
      (this.input("cat", operands, stdin, err) || []).forEach(([, lines]) => out.push(...lines));
    } else if (cmd === "echo") {
      out.push(args.join(" "));
    } else if (cmd === "cp" || cmd === "mv") {
      this.copyMove(cmd, operands, flags, err);
    } else if (cmd === "rm") {
      if (!operands.length) err.push("rm: Operand fehlt");
      operands.forEach((name) => {
        const p = this.resolve(name);
        if (PROTECTED.includes(p)) err.push(`rm: ${name}: das lassen wir lieber`);
        else if (this.isFile(p)) this.nodes.delete(p);
        else if (this.isDir(p)) {
          if (!flags.has("r")) err.push(`rm: ${name}: ist ein Verzeichnis (Tipp: rm -r)`);
          else {
            this.subtree(p).forEach((k) => this.nodes.delete(k));
            if (this.cwd === p || this.cwd.startsWith(p + "/")) this.cwd = parentOf(p);
          }
        } else err.push(`rm: ${name}: nicht gefunden`);
      });
    } else if (cmd === "grep") {
      if (!operands.length) err.push("grep: Suchtext fehlt");
      else {
        const [needle, ...files] = operands;
        const sources = this.input("grep", files, stdin, err) || [];
        const multi = sources.length > 1;
        sources.forEach(([label, lines]) => {
          let count = 0;
          lines.forEach((line, idx) => {
            const hit = flags.has("i") ? line.toLowerCase().includes(needle.toLowerCase()) : line.includes(needle);
            if (hit !== flags.has("v")) {
              count += 1;
              if (!flags.has("c")) out.push((multi ? `${label}:` : "") + (flags.has("n") ? `${idx + 1}:` : "") + line);
            }
          });
          if (flags.has("c")) out.push((multi ? `${label}:` : "") + String(count));
        });
      }
    } else if (cmd === "wc") {
      (this.input("wc", operands, stdin, err) || []).forEach(([label, lines]) => {
        const counts = {
          l: lines.length,
          w: lines.reduce((sum, l) => sum + l.split(" ").filter(Boolean).length, 0),
          c: lines.join("\n").length,
        };
        let chosen = ["l", "w", "c"].filter((k) => flags.has(k));
        if (!chosen.length) chosen = ["l", "w", "c"];
        out.push(chosen.map((k) => String(counts[k])).join(" ") + (label ? ` ${label}` : ""));
      });
    } else if (cmd === "head" || cmd === "tail") {
      let n = 10;
      const files = [];
      for (let i = 0; i < args.length; i++) {
        const a = args[i];
        if (a === "-n" && i + 1 < args.length && isNumber(args[i + 1])) {
          n = parseInt(args[i + 1], 10);
          i += 1;
        } else if (a.startsWith("-") && isNumber(a.slice(1))) n = parseInt(a.slice(1), 10);
        else if (!a.startsWith("-")) files.push(a);
      }
      (this.input(cmd, files, stdin, err) || []).forEach(([, lines]) => {
        out.push(...(cmd === "head" ? lines.slice(0, n) : n ? lines.slice(-n) : []));
      });
    } else if (cmd === "sort") {
      const lines = (this.input("sort", operands, stdin, err) || []).flatMap(([, src]) => src);
      const sorted = [...lines].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
      out.push(...(flags.has("r") ? sorted.reverse() : sorted));
    } else if (cmd === "uniq") {
      (this.input("uniq", operands, stdin, err) || []).forEach(([, lines]) => {
        lines.forEach((line) => {
          if (!out.length || out[out.length - 1] !== line) out.push(line);
        });
      });
    } else if (cmd === "sudo") err.push("Netter Versuch. In der Simulation bist du kein root.");
    else err.push(`${cmd}: Befehl nicht gefunden. Tippe help.`);
    return { out, err, clear: false };
  }

  copyMove(cmd, operands, flags, err) {
    if (operands.length < 2) {
      err.push(`${cmd}: Quelle und Ziel angeben`);
      return;
    }
    const sources = operands.slice(0, -1);
    const dstArg = operands[operands.length - 1];
    if (sources.length > 1 && !this.isDir(this.resolve(dstArg))) {
      err.push(`${cmd}: ${dstArg}: Ziel muss bei mehreren Quellen ein Ordner sein`);
      return;
    }
    sources.forEach((name) => {
      const src = this.resolve(name);
      if (!this.nodes.has(src)) return err.push(`${cmd}: ${name}: nicht gefunden`);
      if (cmd === "mv" && PROTECTED.includes(src)) return err.push(`mv: ${name}: das lassen wir lieber`);
      if (this.isDir(src) && cmd === "cp" && !flags.has("r")) return err.push(`cp: ${name}: ist ein Verzeichnis (Tipp: cp -r)`);
      const target = this.target(src, dstArg);
      if (target === src) return undefined;
      if (target.startsWith(src + "/")) {
        return err.push(`${cmd}: ${name}: kann nicht in sich selbst ${cmd === "mv" ? "verschoben" : "kopiert"} werden`);
      }
      if (!this.isDir(parentOf(target))) return err.push(`${cmd}: ${dstArg}: Zielordner nicht gefunden`);
      if (this.isDir(target)) return err.push(`${cmd}: ${dstArg}: Ziel existiert bereits als Ordner`);
      const keys = this.subtree(src);
      const moved = keys.map((k) => [target + k.slice(src.length), this.nodes.get(k)]);
      if (cmd === "mv") {
        keys.forEach((k) => this.nodes.delete(k));
        if (this.cwd === src || this.cwd.startsWith(src + "/")) this.cwd = target + this.cwd.slice(src.length);
      }
      moved.forEach(([k, v]) => this.nodes.set(k, v));
      return undefined;
    });
  }

  // ---------------------------------------------------------------- entry point
  /** Runs one command line and returns [{kind: "out"|"err"|"clear", text}]. */
  run(rawLine) {
    const line = rawLine.trim().slice(0, MAX_COMMAND_LENGTH);
    if (!line) return [];
    const tokens = tokenize(line);
    if (tokens === null) return [{ kind: "err", text: "Syntaxfehler: Anführungszeichen nicht geschlossen" }];
    const pipeline = [[]];
    let redirect = null;
    for (let i = 0; i < tokens.length; i++) {
      const [kind, value] = tokens[i];
      if (redirect !== null) return [{ kind: "err", text: "Syntaxfehler nach der Umleitung" }];
      if (kind === "word") pipeline[pipeline.length - 1].push(value);
      else if (value === "|") pipeline.push([]);
      else {
        if (i + 1 >= tokens.length || tokens[i + 1][0] !== "word") {
          return [{ kind: "err", text: `Syntaxfehler: Dateiname nach ${value} fehlt` }];
        }
        redirect = [value, tokens[i + 1][1]];
        i += 1;
      }
    }
    if (pipeline.some((seg) => !seg.length)) return [{ kind: "err", text: "Syntaxfehler: leerer Befehl in der Pipe" }];

    const result = [];
    let stdin = null;
    for (const seg of pipeline) {
      const { out, err, clear } = this.exec(seg, stdin);
      if (clear) return [{ kind: "clear", text: "" }];
      err.forEach((e) => result.push({ kind: "err", text: e }));
      stdin = out;
    }

    if (redirect === null) {
      stdin.forEach((o) => result.push({ kind: "out", text: o }));
      return result;
    }
    const [mode, target] = redirect;
    const p = this.resolve(target);
    if (this.isDir(p) || !this.isDir(parentOf(p))) {
      result.push({ kind: "err", text: `Umleitung: ${target}: ungültiges Ziel` });
      return result;
    }
    let text = stdin.join("\n");
    if (mode === ">>" && this.nodes.get(p)) text = this.nodes.get(p) + (text ? "\n" + text : "");
    this.nodes.set(p, text);
    return result;
  }
}
